// src/pages/admin/TemplateDetail.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    IconX,
    IconChevronDown,
    IconUpload,
    IconBellRinging,
    IconCirclePlus,
    IconTrash
} from "@tabler/icons-react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../../assets/images/paqtory.png";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
    fetchCategoriesAndSubcategories,
    fetchSingleTemplate,
    createTemplate,
    updateTemplate
} from "../../redux/slices/templatesSlice";
import Sidebar from "../../components/common/AdminSideBar";
import Header from "../../components/common/AdminHeader";
import AdminLayout from "../../components/common/AdminLayout";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import LoadingFallback from "../../components/common/LoadingFallback";
import ErrorFallback from "../../components/common/ErrorFallback";



/* --------------------------------------------
   Reusable NavItem (Sidebar Link) 
--------------------------------------------- */
function NavItem({ label, to }) {
    const location = useLocation();
    const isActive = location.pathname === to;

    return (
        <Link
            to={to}
            className={`px-3 py-2 rounded-md cursor-pointer flex items-center gap-2
        ${isActive
                    ? "bg-[#FFD500] text-[#0B0C52] font-semibold"
                    : "hover:bg-white/10 text-white"
                }`}
        >
            {label}
        </Link>
    );
}

function TemplateDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { setLayoutConfig } = useAdminLayout();

    const isNewTemplate = id === "new";

    // Redux state
    const {
        categories: categoriesData,
        subcategories: subcategoriesData,
        currentTemplate,
        loading,
        error
    } = useSelector((state) => state.templates);

    // Form state
    const [formData, setFormData] = useState({
        template_name: "",
        category: "",
        sub_category: "",
        status: "DRAFT"
    });

    // Image state
    const [imagePreviews, setImagePreviews] = useState({
        front_side_image: null,
        back_side_image: null
    });
    const [imageFiles, setImageFiles] = useState({
        front_side_image: null,
        back_side_image: null
    });
    const [errors, setErrors] = useState({});

    // Filter subcategories based on selected category
    const getFilteredSubcategories = () => {
        if (!formData.category) return [];
        return subcategoriesData.filter(sub => sub.categoryId == formData.category);
    };

    const handleCategoryChange = (e) => {
        const categoryId = e.target.value;
        setFormData(prev => ({
            ...prev,
            category: categoryId,
            sub_category: "" // Reset subcategory when category changes
        }));
    };

    // Validate image file
    const validateImage = (file) => {
        const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const maxSize = 5 * 1024 * 1024; // 5MB

        if (!validTypes.includes(file.type)) {
            return { valid: false, error: 'Only JPG, PNG, or WEBP images are allowed' };
        }

        if (file.size > maxSize) {
            return { valid: false, error: 'Image size must be less than 5MB' };
        }

        return { valid: true };
    };

    // Handle image upload
    const handleImageUpload = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate the image
        const validation = validateImage(file);
        if (!validation.valid) {
            setErrors(prev => ({
                ...prev,
                media_files: validation.error
            }));
            return;
        }

        // Clear any previous errors
        setErrors(prev => ({ ...prev, media_files: null }));

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreviews(prev => ({
                ...prev,
                [type]: reader.result
            }));
        };
        reader.readAsDataURL(file);

        // Store the original File object
        setImageFiles(prev => ({
            ...prev,
            [type]: file
        }));
    };

    // Handle remove image
    const handleRemoveImage = (type) => {
        setImagePreviews(prev => ({
            ...prev,
            [type]: null
        }));

        setImageFiles(prev => ({
            ...prev,
            [type]: null
        }));
    };

    // Fetch data
    useEffect(() => {
        setLayoutConfig({
            title: `${isNewTemplate ? "Design Template / Add" : "Design Template / Edit"}`,
            showBackButton: true,
            scrollable: true,
        });
        dispatch(fetchCategoriesAndSubcategories());


        if (!isNewTemplate) {
            dispatch(fetchSingleTemplate(id));
        }
    }, [dispatch, id, isNewTemplate, setLayoutConfig]);

    // Update form when template data is loaded
    useEffect(() => {
        if (!isNewTemplate && currentTemplate) {
            setFormData({
                template_name: currentTemplate.template_name,
                category: currentTemplate.category || currentTemplate.category_id,
                sub_category: currentTemplate.sub_category || currentTemplate.sub_category_id,
                status: currentTemplate.status
            });

            // Set image previews if they exist
            if (currentTemplate.media_files && currentTemplate.media_files.length > 0) {
                const mediaFiles = currentTemplate.media_files[0];
                setImagePreviews({
                    front_side_image: mediaFiles.front_image || null,
                    back_side_image: mediaFiles.back_image || null
                });
            }
        }
    }, [currentTemplate, isNewTemplate]);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            // Reset subcategory when category changes
            ...(name === 'category' && { sub_category: '' })
        }));

        // Clear error when field is changed
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form
        const newErrors = {};
        if (!formData.template_name.trim()) newErrors.template_name = "Template name is required";
        if (!formData.category) newErrors.category = "Category is required";
        if (!formData.sub_category) newErrors.sub_category = "Subcategory is required";
        if (!imageFiles.front_side_image && !imagePreviews.front_side_image) {
            newErrors.media_files = "At least front image is required";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Prepare FormData for API
        const formPayload = new FormData();
        formPayload.append('template_name', formData.template_name);
        formPayload.append('category', formData.category);
        formPayload.append('sub_category', formData.sub_category);
        formPayload.append('status', formData.status);

        // Handle image files - only append if they exist
        if (imageFiles.front_side_image) {
            formPayload.append('front_side_image', imageFiles.front_side_image);
        } else if (imagePreviews.front_side_image && typeof imagePreviews.front_side_image === 'string' && !imagePreviews.front_side_image.startsWith('data:')) {
            // If it's a URL (existing image), send the URL
            formPayload.append('front_side_image_url', imagePreviews.front_side_image);
        }

        if (imageFiles.back_side_image) {
            formPayload.append('back_side_image', imageFiles.back_side_image);
        } else if (imagePreviews.back_side_image && typeof imagePreviews.back_side_image === 'string' && !imagePreviews.back_side_image.startsWith('data:')) {
            formPayload.append('back_side_image_url', imagePreviews.back_side_image);
        }

        // Dispatch appropriate action based on new/edit mode
        try {
            if (isNewTemplate) {
                await dispatch(createTemplate(formPayload)).unwrap();
            } else {
                // For update, only send changed fields
                const updatePayload = {};

                if (formData.template_name !== currentTemplate.template_name) {
                    updatePayload.template_name = formData.template_name;
                }
                if (formData.category !== currentTemplate.category) {
                    updatePayload.category = formData.category;
                }
                if (formData.sub_category !== currentTemplate.sub_category) {
                    updatePayload.sub_category = formData.sub_category;
                }
                if (formData.status !== currentTemplate.status) {
                    updatePayload.status = formData.status;
                }

                // Handle images
                if (imageFiles.front_side_image) {
                    updatePayload.front_side_image = imageFiles.front_side_image;
                } else if (imagePreviews.front_side_image && !imagePreviews.front_side_image.startsWith('data:')) {
                    updatePayload.front_side_image_url = imagePreviews.front_side_image;
                }

                if (imageFiles.back_side_image) {
                    updatePayload.back_side_image = imageFiles.back_side_image;
                } else if (imagePreviews.back_side_image && !imagePreviews.back_side_image.startsWith('data:')) {
                    updatePayload.back_side_image_url = imagePreviews.back_side_image;
                }

                await dispatch(updateTemplate({ id, data: updatePayload })).unwrap();
            }
            navigate("/admin/templates");
        } catch (err) {
            console.error("Error saving template:", err);
            setErrors(prev => ({
                ...prev,
                apiError: err.message || "Failed to save template"
            }));
        }
    };

    const filteredSubcategories = getFilteredSubcategories();

    if (loading && !isNewTemplate && !currentTemplate) {
        return <LoadingFallback />
    }

    if (error && !isNewTemplate) {
        return <ErrorFallback error={error} onRetry={() => dispatch(fetchSingleTemplate(id))} />
    }

    return (
        <div>
            <main className="p-6 flex-1 overflow-y-auto">

                <div className="border-2 rounded-md bg-[#FFFFFF] p-6">
                    {errors.apiError && (
                        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                            {errors.apiError}
                        </div>
                    )}
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-6">
                                {/* Template Name */}
                                <div>
                                    <label className="block text-sm font-semibold mb-2 text-[#0B0C52]">
                                        Template Name
                                    </label>
                                    <input
                                        type="text"
                                        name="template_name"
                                        value={formData.template_name}
                                        onChange={handleChange}
                                        className={`w-full border rounded px-3 py-2 ${errors.template_name ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Enter Template Name"
                                    />
                                    {errors.template_name && (
                                        <p className="text-red-500 text-xs mt-1">{errors.template_name}</p>
                                    )}
                                </div>

                                {/* Category */}
                                <div>
                                    <label className="block text-sm font-semibold mb-2 text-[#0B0C52]">
                                        Category
                                    </label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleCategoryChange}
                                        className={`w-full border rounded px-3 py-2 ${errors.category ? 'border-red-500' : 'border-gray-300'}`}
                                    >
                                        <option value="">Select Category</option>
                                        {categoriesData.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.category_name || category.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.category && (
                                        <p className="text-red-500 text-xs mt-1">{errors.category}</p>
                                    )}
                                </div>

                                {/* Subcategory */}
                                <div>
                                    <label className="block text-sm font-semibold mb-2 text-[#0B0C52]">
                                        Subcategory
                                    </label>
                                    <select
                                        name="sub_category"
                                        value={formData.sub_category}
                                        onChange={handleChange}
                                        disabled={!formData.category}
                                        className={`w-full border rounded px-3 py-2 ${errors.sub_category ? 'border-red-500' : 'border-gray-300'}`}
                                    >
                                        <option value="">Select Subcategory</option>
                                        {filteredSubcategories.map(subcategory => (
                                            <option key={subcategory.id} value={subcategory.id}>
                                                {subcategory.sub_category_name || subcategory.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.sub_category && (
                                        <p className="text-red-500 text-xs mt-1">{errors.sub_category}</p>
                                    )}
                                </div>

                                {/* Status */}
                                <div>
                                    <label className="block text-sm font-semibold mb-2 text-[#0B0C52]">
                                        Status
                                    </label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2"
                                    >
                                        <option value="DRAFT">Draft</option>
                                        <option value="PUBLISHED">Published</option>
                                    </select>
                                </div>
                            </div>

                            {/* Right Column - Image Upload */}
                            <div className="space-y-6">
                                {/* Front Image */}
                                <div>
                                    <label className="block text-sm font-semibold mb-2 text-[#0B0C52]">
                                        Front Side Image (Required)
                                    </label>
                                    <div className={`border-2 ${errors.media_files ? 'border-red-500' : 'border-dashed border-gray-300'} rounded-md p-4 flex flex-col items-center justify-center`}>
                                        {imagePreviews.front_side_image ? (
                                            <div className="relative">
                                                <img
                                                    src={imagePreviews.front_side_image}
                                                    alt="Front preview"
                                                    className="max-h-48 mx-auto"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveImage('front_side_image')}
                                                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 -mt-2 -mr-2"
                                                >
                                                    <IconX size={16} />
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <IconUpload size={48} className="text-gray-400 mb-2" />
                                                <p className="text-sm text-gray-500 mb-2">Upload front side image</p>
                                                <label className="bg-[#0B0C52] text-white px-4 py-2 rounded-md cursor-pointer hover:bg-[#0B0C52]/90">
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/jpeg, image/png, image/webp"
                                                        onChange={(e) => handleImageUpload(e, 'front_side_image')}
                                                    />
                                                    Choose File
                                                </label>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Back Image */}
                                <div>
                                    <label className="block text-sm font-semibold mb-2 text-[#0B0C52]">
                                        Back Side Image (Optional)
                                    </label>
                                    <div className="border-2 border-dashed border-gray-300 rounded-md p-4 flex flex-col items-center justify-center">
                                        {imagePreviews.back_side_image ? (
                                            <div className="relative">
                                                <img
                                                    src={imagePreviews.back_side_image}
                                                    alt="Back preview"
                                                    className="max-h-48 mx-auto"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveImage('back_side_image')}
                                                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 -mt-2 -mr-2"
                                                >
                                                    <IconX size={16} />
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <IconUpload size={48} className="text-gray-400 mb-2" />
                                                <p className="text-sm text-gray-500 mb-2">Upload back side image</p>
                                                <label className="bg-[#0B0C52] text-white px-4 py-2 rounded-md cursor-pointer hover:bg-[#0B0C52]/90">
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/jpeg, image/png, image/webp"
                                                        onChange={(e) => handleImageUpload(e, 'back_side_image')}
                                                    />
                                                    Choose File
                                                </label>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {errors.media_files && (
                                    <p className="text-red-500 text-xs">{errors.media_files}</p>
                                )}
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className="flex justify-end gap-4 mt-8 pt-4 border-t border-gray-200">

                            <button
                                type="submit"
                                className="px-4 py-2 bg-[#0B0C52] text-white rounded-md hover:bg-[#0B0C52]/90 flex items-center gap-2"
                            >
                                {isNewTemplate ? (
                                    <>
                                        <IconCirclePlus size={18} />
                                        Create Template
                                    </>
                                ) : (
                                    "Update Template"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </main>
        </div>
    );
}

export default TemplateDetail;